/**
 * screens/VersionHistory.js
 *
 * Version history screen  –  /history/:matchId
 *
 * Layout
 * ──────
 *   ┌──────────────────────────────────────┐  height: 100vh
 *   │  Sticky header (flexShrink: 0)       │
 *   ├──────────────────────────────────────┤
 *   │  Scrollable content (flex: 1,        │
 *   │  overflowY: auto)                    │
 *   │    source badge                      │
 *   │    version cards  …                  │
 *   └──────────────────────────────────────┘
 *
 * Theme
 * ─────
 *   All colours use CSS variables from index.css
 *   (--app-bg, --surface-1/2, --border-1, --text-1/2, --primary-1).
 *   Light/dark-specific overrides that can't be expressed as a single variable
 *   live in index.css under the .version-* class names.
 *   MUI Paper / Dialog / Typography / Button are already themed globally.
 */

import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { SetFullAnalyseString } from "../redux/actions/controlAction";
import { pushCompletedTechQCString, fetchMatchStringById } from "../config/mainRequest";
import BranchVcsPanel from "./components/BranchVcsPanel";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import {
  ArrowBack,
  ContentCopy,
  History,
  CheckCircleOutline,
  AccessTime,
  CloudDone,
  Person,
  Refresh,
} from "@mui/icons-material";
import moment from "moment";
import toast from "react-hot-toast";
import { useFirestoreCommitHistory } from "../persistence/hooks/useFirestoreCommitHistory";
import { declareStringIntent, recordCopiedString } from "../persistence/guard/stringGuard";

// ─── Trigger-type display metadata ───────────────────────────────────────────

const TRIGGER_META = {
  "time":             { label: "Auto · 5 min",    chipColor: "default" },
  "event-count":      { label: "Auto · 10 events", chipColor: "primary" },
  "time+event-count": { label: "Auto · time+events", chipColor: "primary" },
  "api-success":      { label: "Server sync",      chipColor: "success" },
  "manual":           { label: "Manual save",      chipColor: "info"    },
  "exit":             { label: "On exit",          chipColor: "warning" },
  "techqc-edit":      { label: "TechQC · edit",    chipColor: "warning" },
  "techqc-delete":    { label: "TechQC · delete",  chipColor: "error"   },
  "techqc-submit":    { label: "TechQC · submit",  chipColor: "success" },
  "unknown":          { label: "Checkpoint",       chipColor: "default" },
};

// ─── Root screen ─────────────────────────────────────────────────────────────

const VersionHistory = () => {
  const { matchId } = useParams();
  const history     = useHistory();
  const dispatch    = useDispatch();

  const { analystId, token, allMatches } = useSelector((state) => ({
    analystId:  String(state.auth.analyst_id ?? ""),
    token:      state.auth.token,
    allMatches: state.match.all_matches,
  }));

  const matchDetails = allMatches?.find(
    (m) => m.id?.toString() === matchId?.toString()
  );

  const matchTitle = matchDetails
    ? `${matchDetails.team_a_details?.name ?? "?"} vs ${matchDetails.team_b_details?.name ?? "?"}`
    : `Match #${matchId}`;

  const tournamentName = matchDetails?.tournament_name ?? "";

  // ── Multi-analyst tab state ──────────────────────────────────────────────────
  // analysts is the list from the match; default to viewing the current analyst's tab.
  const analysts = useMemo(() => matchDetails?.analyst_details ?? [], [matchDetails]);

  const defaultTabIdx = useMemo(() => {
    const idx = analysts.findIndex((a) => String(a.id) === analystId);
    return idx >= 0 ? idx : 0;
  }, [analysts, analystId]);

  const [selectedAnalystIdx, setSelectedAnalystIdx] = useState(defaultTabIdx);

  // The analyst whose history is shown in the list.
  // Falls back to the current analyst if the tab list is empty (no analyst_details).
  const selectedAnalyst = analysts[selectedAnalystIdx] ?? null;
  const viewingAnalystId = selectedAnalyst ? String(selectedAnalyst.id) : analystId;
  const isViewingOwnHistory = viewingAnalystId === analystId;

  // Other-analyst tabs show that analyst's cloud commits (read-only).  Own
  // history is rendered by BranchVcsPanel, which loads its own commit data — so
  // we only need this hook for the non-own case.
  const { versions, loading, error, refresh } = useFirestoreCommitHistory({
    matchId,
    analystId: viewingAnalystId,
  });

  const [previewVersion,  setPreviewVersion]  = useState(null);
  const [copiedId,        setCopiedId]        = useState(null);

  // Latest match string from the backend — used to mark which commit is the live
  // server version (own history only).  null until fetched.
  const [serverString, setServerString] = useState(null);
  useEffect(() => {
    if (!isViewingOwnHistory || !token) return;
    let cancelled = false;
    (async () => {
      const data = await fetchMatchStringById(matchId, analystId, token);
      if (!cancelled) setServerString(data?.temp_string ?? "");
    })();
    return () => { cancelled = true; };
  }, [isViewingOwnHistory, matchId, analystId, token]);

  // Reset UI state when switching between analysts
  const handleTabChange = useCallback((_e, newIdx) => {
    setSelectedAnalystIdx(newIdx);
    setPreviewVersion(null);
    setCopiedId(null);
  }, []);

  // After a merge/restore is approved, push the merged string back into Redux
  // AND to the server immediately.  The server push is essential: opening the
  // InputBoard navigates to /analyse/:matchId, where Analyse re-fetches the
  // string from the server (get_match_video_string) and overwrites Redux — so
  // without an immediate push the restored string would be clobbered by the old
  // server copy.
  //
  // We use pushCompletedTechQCString with force_update:1 because a restore can
  // bring back an OLDER version with fewer events; a normal push would reject
  // that "downgrade", so force_update makes the server accept the restored string.
  const handleMerged = useCallback((mergedString) => {
    declareStringIntent(matchId, mergedString, "merge");
    dispatch(SetFullAnalyseString({ id: matchId, string: mergedString }));
    if (token) {
      pushCompletedTechQCString(matchId, analystId, token, mergedString, 1)
        .catch((e) => console.warn("[restore] server push failed:", e?.message));
    }
    // The merged string is now the live backend version — update so the
    // matching commit is re-marked "latest" without waiting for a refetch.
    setServerString(mergedString);
    refresh();
  }, [dispatch, matchId, analystId, token, refresh]);

  const handleCopy = useCallback(async (string, id) => {
    recordCopiedString(matchId, string);
    try {
      await navigator.clipboard.writeText(string);
    } catch {
      // clipboard API blocked (e.g. non-HTTPS dev server) — use execCommand
      const ta = document.createElement("textarea");
      ta.value = string;
      Object.assign(ta.style, { position: "fixed", opacity: 0 });
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopiedId(id);
    toast.success("Copied to clipboard", { duration: 2000 });
    setTimeout(() => setCopiedId(null), 2500);
  }, [matchId]);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    // Outer wrapper: fills the viewport, clips overflow so only the inner
    // content area scrolls — the header stays pinned.
    <Box
      className="version-history-page"
      sx={{ height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}
    >

      {/* ── Sticky header ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 1.5, sm: 2, md: 4 },
          py: { xs: 1.5, md: 2 },
          borderBottom: "1px solid var(--border-1)",
          backgroundColor: "var(--surface-1)",
        }}
      >
        {/*
          Layout (single row at all breakpoints):
            [← back]  [title block — flex:1]  [☁ Sync]  [→ Board]
          On xs the button labels are hidden — only the icons remain so the
          title always has room to breathe.  Tooltips cover the icon-only state.
        */}
        <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 1.5 }}>

          {/* Back */}
          <Tooltip title="Back to match list">
            <IconButton
              onClick={() => history.push("/home")}
              size="small"
              sx={{
                border: "1px solid var(--border-1)",
                color: "var(--text-1)",
                flexShrink: 0,
              }}
            >
              <ArrowBack fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Title block — takes all remaining space, truncates gracefully */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <History sx={{ fontSize: 16, color: "var(--text-2)", flexShrink: 0 }} />
              <Typography
                variant="subtitle1"
                fontWeight={700}
                fontFamily="Poppins"
                color="var(--text-1)"
                noWrap
                sx={{ fontSize: { xs: 14, sm: 16 } }}
              >
                Version History
              </Typography>
            </Stack>

            {/* Subtitle — hidden on very small screens to save vertical space */}
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              flexWrap="nowrap"
              sx={{ mt: 0.25, display: { xs: "none", sm: "flex" } }}
            >
              <Typography
                variant="caption"
                fontFamily="Poppins"
                color="var(--text-2)"
                noWrap
                sx={{ maxWidth: { sm: 180, md: 320 } }}
              >
                {matchTitle}
              </Typography>
              {tournamentName && (
                <>
                  <Typography variant="caption" color="var(--text-2)">·</Typography>
                  <Typography variant="caption" fontFamily="Poppins" color="var(--text-2)" noWrap>
                    {tournamentName}
                  </Typography>
                </>
              )}
              <Typography variant="caption" color="var(--text-2)">·</Typography>
              <Typography variant="caption" fontFamily="Poppins" color="var(--text-2)" noWrap>
                #{matchId}
              </Typography>
            </Stack>

            {/* xs-only one-liner: just match ID */}
            <Typography
              variant="caption"
              fontFamily="Poppins"
              color="var(--text-2)"
              noWrap
              sx={{ display: { xs: "block", sm: "none" }, mt: 0.25 }}
            >
              #{matchId} · {matchTitle}
            </Typography>
          </Box>

          {/* ── Action buttons ─────────────────────────────────────────────── */}
          {/* Version-control actions (Save Version / Share / Sync my work / Get)
              live in BranchVcsPanel below; the header only links to InputBoard. */}
          <Stack direction="row" spacing={{ xs: 0.75, sm: 1 }} flexShrink={0} alignItems="center">

            {/* Open InputBoard — only shown when viewing own history
                xs  → icon-only (ArrowForward icon)
                sm+ → full button with label                              */}
            {isViewingOwnHistory && (
              <Tooltip title="Open InputBoard">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => history.push(`/analyse/${matchId}`)}
                  sx={{
                    textTransform: "none",
                    fontFamily: "Poppins",
                    fontSize: { xs: 12, sm: 13 },
                    flexShrink: 0,
                    borderColor: "var(--border-1)",
                    color: "var(--text-1)",
                    "& .MuiButton-endIcon": { ml: { xs: 0, sm: 0.75 } },
                    minWidth: { xs: 32, sm: "auto" },
                    px: { xs: 1, sm: 1.5 },
                  }}
                  endIcon={
                    <ArrowBack
                      sx={{
                        fontSize: "14px !important",
                        transform: "rotate(180deg)",
                      }}
                    />
                  }
                >
                  <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                    Open InputBoard
                  </Box>
                </Button>
              </Tooltip>
            )}

          </Stack>
        </Stack>
      </Box>

      {/* ── Analyst tabs (only when match has multiple analysts) ───────────── */}
      {analysts.length > 1 && (
        <Box
          sx={{
            flexShrink: 0,
            borderBottom: "1px solid var(--border-1)",
            backgroundColor: "var(--surface-1)",
            px: { xs: 1.5, sm: 2, md: 4 },
          }}
        >
          <Tabs
            value={selectedAnalystIdx}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 40,
              "& .MuiTab-root": {
                minHeight: 40,
                textTransform: "none",
                fontFamily: "Poppins",
                fontSize: { xs: 12, sm: 13 },
                color: "var(--text-2)",
                px: { xs: 1.5, sm: 2 },
              },
              "& .Mui-selected": { color: "var(--primary-1) !important" },
              "& .MuiTabs-indicator": { backgroundColor: "var(--primary-1)" },
            }}
          >
            {analysts.map((analyst, idx) => {
              const isMe = String(analyst.id) === analystId;
              const label = analyst.name ?? analyst.username ?? `Analyst ${analyst.id}`;
              return (
                <Tab
                  key={analyst.id}
                  value={idx}
                  icon={<Person sx={{ fontSize: 14, mb: "0 !important" }} />}
                  iconPosition="start"
                  label={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <span>{label}</span>
                      {isMe && (
                        <Chip
                          label="You"
                          size="small"
                          color="primary"
                          sx={{ fontSize: 9, height: 16, fontFamily: "Poppins", pointerEvents: "none" }}
                        />
                      )}
                    </Stack>
                  }
                  sx={{ gap: 0.5 }}
                />
              );
            })}
          </Tabs>
        </Box>
      )}

      {/* ── Scrollable content ──────────────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: { xs: 2, md: 4 },
          py: 2.5,
        }}
      >

        {/* ── Git-style version control panel (own history only) ──────────── */}
        {isViewingOwnHistory && !loading && (
          <BranchVcsPanel
            matchId={matchId}
            analystId={analystId}
            analysts={analysts}
            onMerged={handleMerged}
            serverString={serverString}
          />
        )}

        {/* ── Other analyst's history (read-only) ─────────────────────────────
            Own history is handled by BranchVcsPanel above; this renders the same
            read-only "Saved versions" panel (view + preview + compare, no
            restore/share) for a teammate's cloud commits. */}
        {!isViewingOwnHistory && (
          <>
            {/* Source indicator + reload */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CloudDone sx={{ fontSize: 15, color: "var(--text-2)" }} />
                <Typography variant="caption" color="var(--text-2)" fontFamily="Poppins">
                  Cloud versions (read-only) ·{" "}
                  <strong style={{ color: "var(--text-1)" }}>
                    {analysts[selectedAnalystIdx]?.name ??
                     analysts[selectedAnalystIdx]?.username ??
                     "this analyst"}
                  </strong>
                </Typography>
              </Stack>
              <Tooltip title="Reload">
                <IconButton size="small" onClick={refresh} disabled={loading} sx={{ p: 0.25 }}>
                  <Refresh sx={{ fontSize: 15, color: "var(--text-2)" }} />
                </IconButton>
              </Tooltip>
            </Stack>

            {error && !loading && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Could not load version history: {error}
              </Alert>
            )}

            <BranchVcsPanel
              readOnly
              matchId={matchId}
              analystId={viewingAnalystId}
              commits={versions}
              loading={loading}
              title={
                analysts[selectedAnalystIdx]?.name ??
                analysts[selectedAnalystIdx]?.username ??
                "Analyst"
              }
            />

            <Box sx={{ height: 24 }} />
          </>
        )}
      </Box>

      {/* ── Preview dialog ──────────────────────────────────────────────────── */}
      <PreviewDialog
        version={previewVersion}
        onClose={() => setPreviewVersion(null)}
        onCopy={handleCopy}
        isCopied={copiedId === previewVersion?.id}
      />
    </Box>
  );
};

// ─── VersionCard ──────────────────────────────────────────────────────────────

const VersionCard = ({ version, versionNumber, isLatest, onPreview, onCopy, isCopied }) => {
  const meta       = TRIGGER_META[version.triggerType] ?? TRIGGER_META["unknown"];
  const eventCount = version.eventCount ?? 0;
  const stringLen  = version.string?.length ?? 0;
  const preview    = version.string
    ? version.string.slice(0, 90) + (version.string.length > 90 ? "…" : "")
    : "";

  return (
    <Paper
      elevation={0}
      className={isLatest ? "version-card-latest" : ""}
      sx={{
        border: isLatest ? undefined : "1px solid var(--border-1)",
        borderRadius: 2,
        px: 2.5,
        py: 2,
        backgroundColor: isLatest ? undefined : "var(--surface-1)",
        transition: "box-shadow 0.15s",
        "&:hover": { boxShadow: "var(--shadow-1)" },
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">

        {/* Version bubble */}
        <Box
          className={isLatest ? "version-bubble-latest" : "version-bubble-default"}
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: isLatest ? "#fff" : "var(--text-2)" }}
          >
            v{versionNumber}
          </Typography>
        </Box>

        {/* Card body */}
        <Box sx={{ flex: 1, minWidth: 0 }}>

          {/* Top row: timestamp + badges */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 0.5, sm: 1.5 }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            flexWrap="wrap"
            mb={0.75}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <AccessTime sx={{ fontSize: 13, color: "var(--text-2)" }} />
              <Typography
                variant="body2"
                fontFamily="Poppins"
                fontWeight={600}
                color="var(--text-1)"
              >
                {moment(version.createdAt).format("DD MMM YYYY, HH:mm:ss")}
              </Typography>
            </Stack>

            <Typography
              variant="caption"
              color="var(--text-2)"
              fontFamily="Poppins"
              sx={{ display: { xs: "none", sm: "block" } }}
            >
              {moment(version.createdAt).fromNow()}
            </Typography>

            {isLatest && (
              <Chip
                label="Latest"
                size="small"
                color="primary"
                sx={{ fontSize: 10, height: 20, fontFamily: "Poppins" }}
              />
            )}
          </Stack>

          {/* Stats row: trigger chip + event count + char count */}
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" mb={1}>
            <Chip
              label={meta.label}
              size="small"
              color={meta.chipColor}
              variant="outlined"
              sx={{ fontSize: 11, height: 22, fontFamily: "Poppins",color: "var(--text-1)" }}
            />
            <Typography variant="caption" color="var(--text-2)" fontFamily="Poppins">
              <strong style={{ color: "var(--text-1)" }}>{eventCount}</strong>&nbsp;events
            </Typography>
            <Typography variant="caption" color="var(--text-2)" fontFamily="Poppins">
              {stringLen.toLocaleString()}&nbsp;chars
            </Typography>
            {version.synced && (
              <Tooltip title="Backed up to Firestore">
                <CloudDone sx={{ fontSize: 14, color: "#22c55e" }} />
              </Tooltip>
            )}
          </Stack>

          {/* String preview strip */}
          {preview && (
            <Box
              className="version-string-preview"
              sx={{
                borderRadius: 1,
                px: 1.5,
                py: 0.75,
                fontFamily: "monospace",
                fontSize: 11,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {preview}
            </Box>
          )}
        </Box>

        {/* Action buttons */}
        <Stack spacing={0.75} flexShrink={0}>
          <Button
            size="small"
            variant="outlined"
            onClick={onPreview}
            sx={{
              textTransform: "none",
              fontSize: 12,
              fontFamily: "Poppins",
              minWidth: 76,
              py: 0.4,
              borderColor: "var(--border-1)",
              color: "var(--text-1)",
            }}
          >
            Preview
          </Button>

          <Tooltip title={isCopied ? "Copied!" : "Copy full string to clipboard"}>
            <Button
              size="small"
              variant={isCopied ? "contained" : "outlined"}
              color={isCopied ? "success" : "primary"}
              onClick={onCopy}
              startIcon={
                isCopied
                  ? <CheckCircleOutline sx={{ fontSize: 13 }} />
                  : <ContentCopy sx={{ fontSize: 13 }} />
              }
              sx={{
                textTransform: "none",
                fontSize: 12,
                fontFamily: "Poppins",
                minWidth: 76,
                py: 0.4,
              }}
            >
              {isCopied ? "Copied" : "Copy"}
            </Button>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
};

// ─── PreviewDialog ────────────────────────────────────────────────────────────

const PreviewDialog = ({ version, onClose, onCopy, isCopied }) => {
  if (!version) return null;

  const meta       = TRIGGER_META[version.triggerType] ?? TRIGGER_META["unknown"];
  const eventLines = version.string
    ? version.string
        .split(",")
        .filter((t) => t.trim() && t.trim() !== "X-X-X-X-X-X-X-X-X-X-X-X")
    : [];

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          // MUI Dialog is already globally themed in index.css for dark mode;
          // these ensure the surface token is used in light mode too.
          backgroundColor: "var(--surface-1)",
          color: "var(--text-1)",
          border: "1px solid var(--border-1)",
        },
      }}
    >
      {/* ── Dialog header ────────────────────────────────────────────────── */}
      <DialogTitle
        sx={{
          fontFamily: "Poppins",
          fontWeight: 700,
          fontSize: 15,
          py: 2,
          px: 3,
          borderBottom: "1px solid var(--border-1)",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="subtitle1" fontFamily="Poppins" fontWeight={700} color="var(--text-1)">
              Version checkpoint
            </Typography>
            <Typography variant="caption" color="var(--text-2)" fontFamily="Poppins">
              {moment(version.createdAt).format("DD MMM YYYY, HH:mm:ss")}
              &nbsp;·&nbsp;{meta.label}
              &nbsp;·&nbsp;{version.eventCount ?? 0} events
            </Typography>
          </Box>
          <Chip
            label={`${(version.string?.length ?? 0).toLocaleString()} chars`}
            size="small"
            variant="outlined"
            sx={{
              fontFamily: "Poppins",
              fontSize: 11,
              borderColor: "var(--border-1)",
              color: "var(--text-2)",
              ml: 1,
              flexShrink: 0,
            }}
          />
        </Stack>
      </DialogTitle>

      {/* ── Dialog body ──────────────────────────────────────────────────── */}
      <DialogContent sx={{ p: 0 }}>

        {/* Raw string block */}
        <Box sx={{ p: 2.5, borderBottom: "1px solid var(--border-1)" }}>
          <Typography
            variant="overline"
            sx={{ color: "var(--text-2)", display: "block", mb: 0.75, fontSize: 10 }}
          >
            Raw string
          </Typography>
          <Box
            component="pre"
            className="version-code-block"
            sx={{
              borderRadius: 1,
              p: 2,
              overflow: "auto",
              maxHeight: 160,
              m: 0,
              fontSize: 12,
              lineHeight: 1.6,
              wordBreak: "break-all",
              whiteSpace: "pre-wrap",
            }}
          >
            {version.string || "(empty)"}
          </Box>
        </Box>

        {/* Parsed events */}
        <Box sx={{ p: 2.5 }}>
          <Typography
            variant="overline"
            sx={{ color: "var(--text-2)", display: "block", mb: 1, fontSize: 10 }}
          >
            Parsed events&nbsp;({eventLines.length})
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              maxHeight: 230,
              overflowY: "auto",
            }}
          >
            {eventLines.length === 0 ? (
              <Typography variant="body2" color="var(--text-2)" fontFamily="Poppins">
                No meaningful events in this checkpoint.
              </Typography>
            ) : (
              eventLines.map((event, i) => (
                <Chip
                  key={i}
                  label={`${i + 1}. ${event}`}
                  size="small"
                  variant="outlined"
                  className="version-event-chip"
                  
                  sx={{ height: 22, color: "var(--text-2)", "& .MuiChip-label": { color: "var(--text-2)" } }}
                />
              ))
            )}
          </Box>
        </Box>
      </DialogContent>

      {/* ── Dialog footer ────────────────────────────────────────────────── */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid var(--border-1)",
          gap: 1,
          backgroundColor: "var(--surface-1)",
        }}
      >
        <Typography
          variant="caption"
          color="var(--text-2)"
          fontFamily="Poppins"
          sx={{ flex: 1 }}
        >
          To restore: copy the string, open InputBoard, paste it in.
        </Typography>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{
            textTransform: "none",
            fontFamily: "Poppins",
            borderColor: "var(--border-1)",
            color: "var(--text-1)",
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          color={isCopied ? "success" : "primary"}
          size="small"
          startIcon={isCopied ? <CheckCircleOutline /> : <ContentCopy />}
          onClick={() => onCopy(version.string, version.id)}
          sx={{ textTransform: "none", fontFamily: "Poppins" }}
        >
          {isCopied ? "Copied!" : "Copy String"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VersionHistory;
